import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import api from 'api/api';
import agapi from 'api/agapi';
import '../careiq.css';

/*
  Standardized Modal Architecture for CareIQ
  - Uses both 'api' (BlinkScribe) and 'agapi' (Hub/Clinical)
  - All images proxied via ProxyImage to ensure Auth headers are passed
*/

const BRAND = {
  coral: "#E8653A", navy: "#1B2A4A", slate: "#64748B", emerald: "#10B981", amber: "#F59E0B", blue: "#3B82F6",
  surface: "#FFFFFF", border: "#E2E8F0", borderLight: "#F1F5F9"
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr.includes('/')) {
    const [m, d, y] = dateStr.split('/');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return dateStr.split('T')[0];
};

/* ═══ Shared Components ═══ */

const ModalFrame = ({ title, date, onClose, children, width = 800 }) => {
  useEffect(() => {
    document.body.classList.add('ciqt-no-scroll');
    return () => { document.body.classList.remove('ciqt-no-scroll'); };
  }, []);

  return (
    <div className="ciqt-modal-bg" onClick={onClose}>
      <div className="ciqt-modal" style={{ maxWidth: width, width: "95%" }} onClick={e => e.stopPropagation()}>
        <div className="ciqt-modal-head">
          <div>
            <span className="ciqt-modal-t">{title}</span>
            {date && <div style={{ fontSize: 12, color: BRAND.slate, marginTop: 2 }}>{date}</div>}
          </div>
          <button className="ciqt-modal-x" onClick={onClose}>✕</button>
        </div>
        <div className="ciqt-modal-body" style={{ minHeight: 200, padding: 0 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * ProxyImage handles images that require authentication (e.g., Athena API links).
 * Uses the exact same token logic as the main API instances.
 */
const ProxyImage = ({ src, alt, className }) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;
    if (src.startsWith('data:') || src.startsWith('blob:')) {
      setImgSrc(src);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchImg = async () => {
      setLoading(true); setError(false);
      console.log("[ProxyImage] Fetching authenticated image:", src);
      try {
        const token = sessionStorage.getItem('auth') || localStorage.getItem('auth');
        const response = await axios.get(src, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'blob'
        });
        
        if (!cancelled) {
          const objectUrl = URL.createObjectURL(response.data);
          setImgSrc(objectUrl);
        }
      } catch (err) {
        console.error("Failed to load authenticated image:", src, err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchImg();
    return () => { 
      cancelled = true; 
      if (imgSrc && imgSrc.startsWith('blob:')) URL.revokeObjectURL(imgSrc);
    };
  }, [src]);

  if (loading) return <div className="ciqt-visit-loading" style={{ padding: 20 }}><span>Loading Page...</span></div>;
  if (error) return <div className="ciqt-empty" style={{ padding: 20 }}>Failed to load image.</div>;

  return <img src={imgSrc} alt={alt} className={className} />;
};

/* ═══ Visit Summary Modal ═══ */
export const VisitSummaryModal = ({ eventData, patientId, onClose }) => {
  const [activeTab, setActiveTab] = useState("blinkscribe");
  const [bsLoading, setBsLoading] = useState(false);
  const [bsRecord, setBsRecord] = useState(null);
  const [esLoading, setEsLoading] = useState(false);
  const [esData, setEsData] = useState(null);
  const [hwLoading, setHwLoading] = useState(false);
  const [hwPages, setHwPages] = useState(null);
  const [hwMatch, setHwMatch] = useState(null);
  const [hwError, setHwError] = useState(false);

  const visitDate = eventData?.date;
  const encounterId = eventData?.details?.encounter_summary?.encounter_id || eventData?.encounter_id || eventData?.id;
  const practiceId = sessionStorage.getItem('practiceId') || localStorage.getItem('practiceId') || "9928";

  // 1. Fetch BlinkScribe Note
  useEffect(() => {
    const formattedDate = formatDate(visitDate);
    if (!patientId || !formattedDate) return;
    let cancelled = false;
    const fetchBS = async () => {
      setBsLoading(true);
      try {
        const res = await api.post(`/v1/patient/${patientId}/recording/details`, {
          filter_array: { date_filter: { start_date: formattedDate, end_date: formattedDate } }
        });
        if (!cancelled) {
          const data = res?.data || res;
          setBsRecord(Array.isArray(data) && data.length > 0 ? data[0] : null);
        }
      } finally { if (!cancelled) setBsLoading(false); }
    };
    fetchBS();
    return () => { cancelled = true; };
  }, [patientId, visitDate]);

  // 2. Check for matching Handwritten (Progress) Note
  useEffect(() => {
    const formattedDate = formatDate(visitDate);
    if (!patientId || !formattedDate) return;
    let cancelled = false;
    const checkHW = async () => {
      try {
        const res = await agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`, {
          category: "ProgressNotes", department_id: 1
        });
        const docs = res.data?.encounterdocuments || res.data?.data || [];
        
        // Strict match: by encounterId first
        let match = encounterId ? docs.find(d => String(d.encounterid) === String(encounterId)) : null;
        
        // Fallback: match by date if encounterId not found or not provided
        if (!match && formattedDate) {
           match = docs.find(d => {
             const dStr = (d.createddate || d.createddatetime || "");
             return dStr.startsWith(formattedDate);
           });
        }
        if (!cancelled) setHwMatch(match || null);
      } catch (err) { console.error("Check HW Error:", err); }
    };
    checkHW();
    return () => { 
      cancelled = true; 
      setHwMatch(null); // Clear match on unmount/change to prevent "same note" issue
      setHwPages(null);
    };
  }, [patientId, visitDate, encounterId, practiceId]);

  // 3. Fetch Encounter Summary
  useEffect(() => {
    if (activeTab !== "summary" || !encounterId) return;
    if (esData) return;
    let cancelled = false;
    const fetchES = async () => {
      setEsLoading(true);
      try {
        // Match CareIQTab.jsx specific-summary call
        const res = await agapi.post(`/v1/fhir-mirror/${practiceId}/encounter/specific-summary`, {
          encounter_id: encounterId,
          patient_id: parseInt(patientId)
        });
        if (!cancelled) setEsData(res?.data?.data || res?.data || res);
      } finally { if (!cancelled) setEsLoading(false); }
    };
    fetchES();
    return () => { cancelled = true; };
  }, [activeTab, encounterId, patientId, practiceId, esData]);

  // 4. Fetch Handwritten Note content
  useEffect(() => {
    if (activeTab !== "handwritten" || !hwMatch) return;
    if (hwPages) return;
    let cancelled = false;
    const fetchHWContent = async () => {
      setHwLoading(true); setHwError(false);
      try {
        const res = await agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/image-result`, {
          patientid: patientId,
          imagingresultid: hwMatch.encounterdocumentid || hwMatch.id,
          category: "ProgressNotes",
          page_offset: 0,
          page_limit: 20
        });
        const data = res.data?.data || res.data || res;
        const pages = (Array.isArray(data) ? data[0]?.pages : data?.pages) || [];
        if (!cancelled) setHwPages(pages);
      } catch (err) {
        console.error("Fetch HW Content Error:", err);
        if (!cancelled) setHwError(true);
      } finally { if (!cancelled) setHwLoading(false); }
    };
    fetchHWContent();
    return () => { cancelled = true; };
  }, [activeTab, hwMatch, patientId, practiceId, hwPages]);

  return (
    <ModalFrame title={eventData?.details?.encounter_summary?.type || "Visit Details"} date={visitDate} onClose={onClose}>
      <div className="ciqt-visit-tabs">
        <button className={`ciqt-visit-tab ${activeTab === "blinkscribe" ? "ciqt-visit-tab-active" : ""}`} onClick={() => setActiveTab("blinkscribe")}>📝 BlinkScribe Note</button>
        <button className={`ciqt-visit-tab ${activeTab === "summary" ? "ciqt-visit-tab-active" : ""}`} onClick={() => setActiveTab("summary")}>📋 Encounter Summary</button>
        {hwMatch && (
          <button className={`ciqt-visit-tab ${activeTab === "handwritten" ? "ciqt-visit-tab-active" : ""}`} onClick={() => setActiveTab("handwritten")}>✍️ Handwritten Note</button>
        )}
      </div>

      <div style={{ padding: 20 }}>
        {activeTab === "blinkscribe" && (
          bsLoading ? <div className="ciqt-visit-loading">Loading note…</div> :
          bsRecord ? (
            <div className="ciqt-bs-note">
              {["subjective", "hpi", "ros", "physical_exam", "assessment_plan"].map(key => bsRecord[key] && (
                <div key={key} className="ciqt-bs-section">
                  <div className="ciqt-bs-section-label">{key.replace('_', ' ').toUpperCase()}</div>
                  <div className="ciqt-bs-section-text" style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: bsRecord[key] }} />
                </div>
              ))}
            </div>
          ) : <div className="ciqt-empty">No BlinkScribe note found for this date.</div>
        )}

        {activeTab === "summary" && (
          esLoading ? <div className="ciqt-visit-loading">Loading summary…</div> :
          esData ? (
            <div className="ciqt-summary-container">
               {esData.summaryhtml ? (
                 <div className="ciqt-summary-html" dangerouslySetInnerHTML={{ __html: esData.summaryhtml }} />
               ) : (
                 <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", background: "#f8fafc", padding: 12, borderRadius: 8 }}>
                   {JSON.stringify(esData, null, 2)}
                 </pre>
               )}
            </div>
          ) : <div className="ciqt-empty">Encounter summary not available.</div>
        )}

        {activeTab === "handwritten" && (
          hwLoading ? <div className="ciqt-visit-loading">Loading handwritten note…</div> :
          hwError ? <div className="ciqt-empty">Failed to load handwritten note.</div> :
          hwPages && hwPages.length > 0 ? (
            <div className="ciqt-doc-pages">
              {hwPages.map((page, i) => (
                <div key={i} className="ciqt-doc-page">
                   <div className="ciqt-doc-page-num">Page {i + 1}</div>
                   <ProxyImage src={page.data.startsWith('data:') ? page.data : `data:image/jpeg;base64,${page.data}`} alt="Handwritten Page" className="ciqt-doc-img" />
                </div>
              ))}
            </div>
          ) : <div className="ciqt-empty">No pages found.</div>
        )}
      </div>
    </ModalFrame>
  );
};

/* ═══ Labs Modal ═══ */
export const LabsMiniModal = ({ labGroup, onClose, onSwitchToLabs }) => {
  return (
    <ModalFrame title={`🧪 ${labGroup.label}`} date={labGroup.date} onClose={onClose} width={500}>
      <div style={{ padding: 20 }}>
        <div className="ciqt-labs-list">
          {labGroup.tests.map((test, i) => (
            <div key={i} className="ciqt-lab-item">
              <div className="ciqt-lab-name">{test.name}</div>
              <div className={`ciqt-lab-val ${test.isAbnormal ? 'abnormal' : ''}`}>
                {test.value} <span className="ciqt-lab-unit">{test.unit}</span>
                {test.isAbnormal && <span className="ciqt-lab-alert">⚠</span>}
              </div>
            </div>
          ))}
        </div>
        <button className="ciqt-btn-primary" style={{ width: "100%", marginTop: 20 }} onClick={onSwitchToLabs}>View Cumulative Trends</button>
      </div>
    </ModalFrame>
  );
};

/* ═══ Imaging Modal ═══ */
export const ImagingModal = ({ eventData, patientId, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const [imgData, setImgData] = useState(null);
  const practiceId = data?.config?.practiceid;

  useEffect(() => {
    if (!patientId || !practiceId || !eventData) return;
    let cancelled = false;
    const fetchImaging = async () => {
      setLoading(true);
      try {
        const docId = eventData.imagingresultid || eventData.id;
        
        console.log("[ImagingModal] imagingresultid:", docId, "| full raw:", eventData);
        
        // Guard: do NOT call the API if we have no imagingresultid — it will 422
        if (!docId) {
          console.warn("[ImagingModal] imagingresultid is null/undefined. Cannot fetch image.");
          if (!cancelled) setImgData({ error: "No imaging result ID found for this document." });
          return;
        }

        const payload = {
          patientid: patientId,
          imagingresultid: docId,
          category: "Procedures",
          page_offset: 0,
          page_limit: 20,
        };
        console.log("[ImagingModal] 🚀 Calling image-result API with payload:", payload);

        const res = await agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/image-result`, payload);
        if (!cancelled) {
          const raw = res?.data?.data || res?.data || res;
          setImgData(raw);
        }
      } catch (err) {
        console.error("[ImagingModal] Error:", err);
        if (!cancelled) setImgData({ error: "Failed to load imaging details." });
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchImaging();
    return () => { cancelled = true; };
  }, [patientId, practiceId, eventData]);

  return (
    <ModalFrame title="🏥 Imaging Results" date={eventData?.date} onClose={onClose}>
      <div style={{ padding: 20 }}>
        {loading ? <div className="ciqt-visit-loading">Loading imaging…</div> :
        imgData ? (
          <div className="ciqt-img-grid">
            {imgData.error ? (
              <div style={{ padding: 20, textAlign: "center", color: "#DC2626", background: "#FEF2F2", borderRadius: 8 }}>
                <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
                {imgData.error}
              </div>
            ) : (
              (Array.isArray(imgData) ? imgData : [imgData]).map((item, i) => (
                <div key={i} className="ciqt-img-card">
                  <div className="ciqt-img-title">{item.title || item.name || item.documentdescription || "Imaging Result"}</div>
                  {item.content && <div className="ciqt-img-text" dangerouslySetInnerHTML={{ __html: item.content }} />}
                  {(item.data || item.base64 || item.image_links || item.pages) && (
                      <div style={{ marginTop: 10 }}>
                          {item.image_links ? item.image_links.map((link, j) => <ProxyImage key={j} src={link} alt="Imaging" className="ciqt-img-doc" />) :
                           item.pages ? item.pages.map((p, j) => <ProxyImage key={j} src={p.data.startsWith('data:') ? p.data : `data:image/jpeg;base64,${p.data}`} alt="Page" className="ciqt-img-doc" />) :
                          <ProxyImage src={(item.data || item.base64).startsWith('data:') ? (item.data || item.base64) : `data:image/jpeg;base64,${item.data || item.base64}`} alt="Result" />
                          }
                      </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : <div className="ciqt-empty">No data available.</div>}
      </div>
    </ModalFrame>
  );
};

/* ═══ Document Modal ═══ */
export const DocumentModal = ({ docItem, patientId, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const [docData, setDocData] = useState(null);
  const practiceId = data?.config?.practiceid;

  useEffect(() => {
    if (!patientId || !docItem) return;
    let cancelled = false;
    const fetchDoc = async () => {
      setLoading(true);
      try {
        const docId = docItem.imagingresultid || docItem.encounterdocumentid || docItem.encounter_document_id || docItem.id;
        const category = docItem.imagingresultid ? "Procedures" : "ProgressNotes";
        
        console.log("[DocumentModal] docId:", docId, "| category:", category, "| raw:", docItem);
        
        // Guard: do NOT call the API if we have no ID — it will 422
        if (!docId) {
          console.warn("[DocumentModal] No document ID found.");
          if (!cancelled) setDocData({ error: "No document ID found for this item." });
          return;
        }

        const payload = {
          patientid: patientId,
          imagingresultid: docId,
          category: category,
          page_offset: 0,
          page_limit: 20,
        };
        console.log("[DocumentModal] 🚀 Calling clinical image-result API:", payload);

        const res = await agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/image-result`, payload);
        
        if (!cancelled) {
          const raw = res.data?.data || res.data || res;
          setDocData(raw);
        }
      } catch (err) {
        console.error("[DocumentModal] Clinical API failed, trying fallback:", err);
        // Fallback to BlinkScribe if it's a legacy document
        try {
            const fallbackId = docItem.encounter_document_id || docItem.encounterdocumentid || docItem.id;
            const fallback = await api.post('/v1/patients/encounter-document/by-id', {
                patient_id: patientId,
                encounter_document_id: fallbackId,
            });
            if (!cancelled) {
              const resData = fallback.data?.data || fallback.data || fallback;
              console.log("[DocumentModal] Fallback Success:", resData);
              setDocData(resData);
            }
        } catch (fErr) {
            console.error("[DocumentModal] Fallback also failed:", fErr);
            if (!cancelled) setDocData({ error: "Failed to load document content." });
        }
      } finally { if (!cancelled) setLoading(false); }
    };
    fetchDoc();
    return () => { cancelled = true; };
  }, [patientId, docItem, practiceId]);

  const renderContent = (data) => {
    let items = [];
    if (Array.isArray(data)) {
      items = data.flatMap(d => {
        if (d.image_links) return d.image_links;
        if (d.pages) return [d];
        if (d.data) return [d];
        return [];
      });
    } else if (data?.image_links) {
      items = data.image_links;
    } else if (data?.pages) {
      items = [data]; 
    } else if (data?.data) {
      items = [data];
    }

    if (items.length > 0) {
      return (
        <div className="ciqt-doc-pages">
          {items.map((item, i) => {
            const isLink = typeof item === 'string';
            
            // If it's a page object from image-result API
            if (!isLink && item.pages) {
              return item.pages.map((p, j) => (
                <div key={`${i}-${j}`} className="ciqt-doc-page">
                   <div className="ciqt-doc-page-num">Page {j + 1}</div>
                   <ProxyImage src={p.data.startsWith('data:') ? p.data : `data:image/jpeg;base64,${p.data}`} alt="Page" className="ciqt-doc-img" />
                </div>
              ));
            }

            const isPdf = !isLink && (item.content_type || "").includes("pdf");
            return (
              <div key={i} className="ciqt-doc-page">
                <div className="ciqt-doc-page-num">Page {i + 1}</div>
                {isPdf ? (
                  <iframe src={item.data.startsWith('data:') ? item.data : `data:application/pdf;base64,${item.data}`} title="PDF" className="ciqt-doc-iframe" />
                ) : (
                  <ProxyImage src={isLink ? item : (item.data.startsWith('data:') ? item.data : `data:image/jpeg;base64,${item.data}`)} alt="Document Page" className="ciqt-doc-img" />
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return <div className="ciqt-bs-section-text" dangerouslySetInnerHTML={{ __html: typeof data === 'string' ? data : JSON.stringify(data, null, 2) }} />;
  };

  return (
    <ModalFrame title={`📄 ${docItem?.title || docItem?.documentdescription || "Document"}`} date={docItem?.date} onClose={onClose}>
      <div style={{ padding: 20 }}>
        {loading ? <div className="ciqt-visit-loading">Loading document…</div> :
        docData ? renderContent(docData) : <div className="ciqt-empty">Document content not available.</div>}
      </div>
    </ModalFrame>
  );
};
